import React, { useEffect, useMemo, useState } from 'react';
import { masterAdminApi } from '../services/api';

const STATUS_OPTIONS = ['all', 'pending', 'completed', 'failed', 'cancelled'];
const TYPE_OPTIONS = ['all', 'subscription', 'pos_transaction'];

function formatMoney(amount, currency = 'NGN') {
    const numeric = Number(amount || 0);
    return `${currency} ${numeric.toLocaleString()}`;
}

function formatDate(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
}

export default function MasterBillingPage() {
    const [loadingOverview, setLoadingOverview] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [overviewRows, setOverviewRows] = useState([]);
    const [historyRows, setHistoryRows] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState('');

    const [historyFilter, setHistoryFilter] = useState({
        status: 'all',
        paymentType: 'all',
        from: '',
        to: '',
    });

    const totals = useMemo(() => {
        return overviewRows.reduce((acc, row) => {
            acc.totalBilling += Number(row.billingAmount || 0);
            acc.totalCompleted += Number(row.completedPaymentsAmount || 0);
            acc.totalPending += Number(row.pendingPaymentsAmount || 0);
            if (row.billingState === 'overdue') acc.overdueStores += 1;
            return acc;
        }, {
            totalBilling: 0,
            totalCompleted: 0,
            totalPending: 0,
            overdueStores: 0,
        });
    }, [overviewRows]);

    const loadOverview = async () => {
        setLoadingOverview(true);
        const response = await masterAdminApi.billingOverview();
        if (!response.success) {
            window.alert(response.error || 'Could not load billing overview');
            setOverviewRows([]);
            setLoadingOverview(false);
            return;
        }

        const rows = Array.isArray(response.data) ? response.data : [];
        setOverviewRows(rows);
        if (rows.length > 0 && !selectedTenantId) {
            setSelectedTenantId(String(rows[0].tenantId));
        }
        setLoadingOverview(false);
    };

    const loadHistory = async () => {
        setLoadingHistory(true);

        const response = await masterAdminApi.billingHistory({
            tenantId: selectedTenantId || undefined,
            status: historyFilter.status === 'all' ? undefined : historyFilter.status,
            paymentType: historyFilter.paymentType === 'all' ? undefined : historyFilter.paymentType,
            from: historyFilter.from || undefined,
            to: historyFilter.to || undefined,
            limit: 300,
        });

        if (!response.success) {
            window.alert(response.error || 'Could not load billing history');
            setHistoryRows([]);
            setLoadingHistory(false);
            return;
        }

        setHistoryRows(Array.isArray(response.data) ? response.data : []);
        setLoadingHistory(false);
    };

    useEffect(() => {
        loadOverview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTenantId, historyFilter.status, historyFilter.paymentType, historyFilter.from, historyFilter.to]);

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Master Billing & Payments</h2>
                    <p>Track subscriptions, invoices, and payment history per store</p>
                </div>
                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button" onClick={loadOverview}>↻</button>
                </div>
            </div>

            <section className="products-shell stock-shell">
                <div className="stats-grid">
                    <div className="stat-card">
                        <h4>Total Recurring Billing</h4>
                        <h3>{formatMoney(totals.totalBilling)}</h3>
                    </div>
                    <div className="stat-card">
                        <h4>Completed Payments</h4>
                        <h3>{formatMoney(totals.totalCompleted)}</h3>
                    </div>
                    <div className="stat-card">
                        <h4>Pending Payments</h4>
                        <h3>{formatMoney(totals.totalPending)}</h3>
                    </div>
                    <div className="stat-card">
                        <h4>Overdue Stores</h4>
                        <h3>{totals.overdueStores}</h3>
                    </div>
                </div>

                <div className="panel stock-panel">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3>Store Subscription Billing Overview</h3>
                        <label style={{ maxWidth: 280 }}>
                            <span>Store Filter</span>
                            <select value={selectedTenantId} onChange={(e) => setSelectedTenantId(e.target.value)}>
                                <option value="">All stores</option>
                                {overviewRows.map((row) => (
                                    <option key={row.tenantId} value={row.tenantId}>{row.tenantName}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="products-table-wrap">
                        <table className="products-table admin-table">
                            <thead>
                                <tr>
                                    <th>Store</th>
                                    <th>Plan</th>
                                    <th>Billing Amount</th>
                                    <th>Next Billing Date</th>
                                    <th>Billing State</th>
                                    <th>Payments</th>
                                    <th>Completed Amount</th>
                                    <th>Pending Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingOverview ? (
                                    <tr><td colSpan={8} className="dash-empty-row">Loading billing overview...</td></tr>
                                ) : overviewRows.length === 0 ? (
                                    <tr><td colSpan={8} className="dash-empty-row">No billing data found</td></tr>
                                ) : overviewRows
                                    .filter((row) => !selectedTenantId || String(row.tenantId) === String(selectedTenantId))
                                    .map((row) => (
                                        <tr key={row.tenantId}>
                                            <td>{row.tenantName}</td>
                                            <td>{row.subscriptionPlan || '-'}</td>
                                            <td>{formatMoney(row.billingAmount)}</td>
                                            <td>{formatDate(row.nextBillingDate)}</td>
                                            <td>{row.billingState}</td>
                                            <td>{row.paymentsCount}</td>
                                            <td>{formatMoney(row.completedPaymentsAmount)}</td>
                                            <td>{formatMoney(row.pendingPaymentsAmount)}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="panel stock-panel">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <h3>Payment History</h3>
                        <div className="products-toolbar-actions" style={{ gap: 10, flexWrap: 'wrap' }}>
                            <label>
                                <span>Status</span>
                                <select
                                    value={historyFilter.status}
                                    onChange={(e) => setHistoryFilter((prev) => ({ ...prev, status: e.target.value }))}
                                >
                                    {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </label>
                            <label>
                                <span>Type</span>
                                <select
                                    value={historyFilter.paymentType}
                                    onChange={(e) => setHistoryFilter((prev) => ({ ...prev, paymentType: e.target.value }))}
                                >
                                    {TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                                </select>
                            </label>
                            <label>
                                <span>From</span>
                                <input
                                    type="date"
                                    value={historyFilter.from}
                                    onChange={(e) => setHistoryFilter((prev) => ({ ...prev, from: e.target.value }))}
                                />
                            </label>
                            <label>
                                <span>To</span>
                                <input
                                    type="date"
                                    value={historyFilter.to}
                                    onChange={(e) => setHistoryFilter((prev) => ({ ...prev, to: e.target.value }))}
                                />
                            </label>
                        </div>
                    </div>

                    <div className="products-table-wrap">
                        <table className="products-table admin-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Store</th>
                                    <th>Payment ID</th>
                                    <th>Type</th>
                                    <th>Method</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Reference</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingHistory ? (
                                    <tr><td colSpan={8} className="dash-empty-row">Loading payment history...</td></tr>
                                ) : historyRows.length === 0 ? (
                                    <tr><td colSpan={8} className="dash-empty-row">No payments found for selected filter</td></tr>
                                ) : historyRows.map((row) => (
                                    <tr key={row.id}>
                                        <td>{formatDate(row.createdAt)}</td>
                                        <td>{row.tenantName || '-'}</td>
                                        <td>{row.paymentId || '-'}</td>
                                        <td>{row.paymentType}</td>
                                        <td>{row.paymentMethod}</td>
                                        <td>{row.status}</td>
                                        <td>{formatMoney(row.amount, row.currency)}</td>
                                        <td>{row.referenceNumber || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}
